import type { Artwork, ArtworkCollection, LocalizedText } from "./artwork-types";

/**
 * Searching the catalogue, in whatever language the customer happens to think in.
 *
 * The point of this being cross-locale is not tidiness. A Slovenian customer
 * types "sunflowers" because that is the name they have seen on a poster; a
 * Pole types "Van Gogh" because a painter's name has no translation. Matching
 * only the locale currently on screen would fail both, and the catalogue is
 * small enough that there is no reason to.
 *
 * So every language a work has a title in is indexed, plus the artist and the
 * year. Diacritics are folded, because nobody reaching for a search box types
 * "Sončnice" with the caron — and a Czech keyboard would not produce it anyway.
 */

/**
 * Letters that are not an accent plus a base letter, so NFD leaves them alone.
 *
 * Polish ł and Croatian đ are the ones that matter here — both are markets
 * this shop sells into, and both would otherwise be unfindable by anyone
 * typing on a keyboard that cannot produce them. The rest are cheap insurance
 * for artist names.
 */
const STROKED: Record<string, string> = {
  ł: "l",
  đ: "d",
  ø: "o",
  ß: "ss",
  æ: "ae",
  œ: "oe",
  ð: "d",
  þ: "th",
  ı: "i",
};

/** Lowercase, strip accents, collapse whitespace. */
export function fold(value: string): string {
  return (value ?? "")
    .toLowerCase()
    .normalize("NFD")
    // Combining marks: č -> c, é -> e, ō -> o.
    .replace(/[̀-ͯ]/g, "")
    // Stroked and ligature letters, which decomposition does not touch.
    .replace(/[łđøßæœðþı]/g, (ch) => STROKED[ch] ?? ch)
    .replace(/\s+/g, " ")
    .trim();
}

/** Every value of a localized field, in every language it has. */
function allValues(text: LocalizedText | undefined | null): string[] {
  return Object.values(text ?? {}).filter(
    (v): v is string => typeof v === "string" && v.length > 0,
  );
}

/**
 * One folded haystack per work. Built once per artwork and reused across
 * keystrokes — rebuilding 150 of these on every character typed is the easy
 * way to make a search box feel broken.
 */
export function searchIndex(artwork: Artwork): string {
  const parts: string[] = [
    ...allValues(artwork.title),
    artwork.artist ?? "",
    artwork.year ? String(artwork.year) : "",
    ...(artwork.collections ?? []).flatMap((c: ArtworkCollection) =>
      allValues(c.title),
    ),
  ];
  return fold(parts.join(" "));
}

/**
 * Whether a work matches, given a pre-built index.
 *
 * Tokens are ANDed and matched as substrings, so "monet water" narrows to
 * Monet's water pieces, and "gogh" finds "Vincent van Gogh" without the
 * customer having to know where the surname starts.
 */
export function matches(index: string, query: string): boolean {
  const tokens = fold(query).split(" ").filter(Boolean);
  if (!tokens.length) return true;
  return tokens.every((token) => index.includes(token));
}

/** Filter a list, preserving its existing order. */
export function filterArtworks<T extends Artwork>(
  artworks: T[],
  query: string,
  indexOf: (artwork: T) => string,
): T[] {
  if (!fold(query)) return artworks;
  return artworks.filter((artwork) => matches(indexOf(artwork), query));
}
